<template>
  <APageHead
    title="대시보드"
    sub="파인애플 아카데미 운영 현황을 한눈에 확인하세요."
  />

  <div class="dash-page">
    <!-- Stats Row -->
    <div class="dash-stats">
      <AStat
        label="전체 교재"
        :value="stats.textbooks"
        icon="menu_book"
        :delta="stats.textbooksChange || null"
        delta-unit="권"
        :hint="stats.textbooksChange ? '지난달 대비' : '내 저작물 전체'"
      />
      <AStat
        label="문의 접수"
        :value="stats.inquiries"
        icon="contact_support"
        :hint="stats.pendingInquiries > 0 ? `${stats.pendingInquiries}건 대기 중` : '모두 처리완료'"
        :accent="stats.pendingInquiries > 0"
      />
      <!-- 지급 완료(buy_invoice.paid_at) 기준 금액 — 정산_프론트엔드개발_가이드.md §12.1 -->
      <AStat
        label="이번달 지급액"
        :value="formatCurrency(stats.monthlySettlement)"
        :delta="stats.settlementChange"
        delta-unit="%"
        icon="request_quote"
        :hint="`이번 달 지급 완료 ${stats.settlementCount}건 · 전월 대비`"
      />
    </div>

    <!-- Charts Row -->
    <div class="dash-charts">
      <!-- 교재별 판매 비중 -->
      <ACard title="교재별 판매 비중" sub="인기 콘텐츠 TOP 5 기준" :padding="20">
        <div v-if="contentShare.length" class="dash-pie">
          <div class="dash-pie__chart">
            <APieChart
              v-if="chartDataReady"
              :key="'pie-' + chartKey"
              id="contents-pie-chart"
              :height="220"
              :chart="contentShareChart"
            />
            <div v-else class="dash-loading">
              <div class="p-spinner" />
            </div>
          </div>
          <ul class="dash-legend">
            <li v-for="item in contentShare" :key="item.title">
              <span class="dash-legend__dot" :style="{ background: item.color }" />
              <span class="dash-legend__label" :title="item.title">{{ item.title }}</span>
              <span class="dash-legend__value">{{ item.percent }}%</span>
            </li>
          </ul>
        </div>
        <AEmpty v-else title="판매량 데이터가 없습니다" />

        <template #foot>
          <p class="dash-card-foot">{{ contentShareFoot }}</p>
        </template>
      </ACard>

      <!-- Monthly Trend Line -->
      <ACard title="월별 정산 추이" sub="최근 6개월" :padding="20">
        <div class="dash-line-legend">
          <span class="dash-line-legend__item">
            <span class="dash-legend__dot dash-legend__dot--point" />
            정산 금액 (만원)
          </span>
          <span class="dash-line-legend__item">
            <span class="dash-legend__dot dash-legend__dot--info" />
            정산 건수
          </span>
        </div>

        <div class="dash-line__wrap">
          <ALineChart
            v-if="chartDataReady && monthlyTrend.length"
            :key="'line-' + chartKey"
            id="settlement-line-chart"
            :height="280"
            :chart="settlementChartData"
          />
          <AEmpty v-else title="정산 데이터가 없습니다" />
        </div>
      </ACard>
    </div>

    <!-- Top Contents + Recent Inquiries Row -->
    <div class="dash-mid">
      <ACard title="인기 콘텐츠 TOP 5" sub="전월까지 정산 집계 기준" :padding="0">
        <template #actions>
          <span v-if="lastLoadedAt" class="dash-updated">
            마지막 갱신 {{ formatTime(lastLoadedAt) }}
          </span>
          <ABtn kind="ghost" size="sm" icon="refresh" :disabled="loading" @click="loadDashboardData">
            새로고침
          </ABtn>
        </template>

        <ATable
          :columns="topContentsCols"
          :rows="topContents"
          empty-text="콘텐츠 판매 데이터가 없습니다"
          row-key="title"
        >
          <template #cell:rank="{ index }">
            <span class="dash-rank" :class="{ 'dash-rank--top': index < 3 }">
              {{ index + 1 }}
            </span>
          </template>

          <template #cell:title="{ row }">
            <div class="dash-content-cell">
              <div class="dash-content-cell__title">{{ row.title }}</div>
              <div class="dash-content-cell__sub">{{ row.author || '-' }}</div>
            </div>
          </template>

          <template #cell:type="{ value }">
            <APill :kind="getContentTypeKind(value)" :icon="getContentTypeMatIcon(value)">
              {{ getContentTypeLabel(value) }}
            </APill>
          </template>

          <template #cell:sales="{ value }">
            <span v-if="!settlementAggregated || value == null" class="dash-mono dash-muted">-</span>
            <span v-else class="dash-mono">{{ value.toLocaleString() }}</span>
          </template>
        </ATable>

        <template v-if="!settlementAggregated" #foot>
          <p class="dash-card-foot">{{ settlementPendingNotice }}</p>
        </template>
      </ACard>

      <ACard title="최근 문의" sub="신규 접수" :padding="0">
        <template #actions>
          <router-link to="/helpdesk/inquiry" class="dash-link">
            전체보기 <i class="material-icons-round">arrow_forward</i>
          </router-link>
        </template>

        <ul v-if="recentInquiries.length" class="dash-inquiries">
          <li
            v-for="(inquiry, index) in recentInquiries"
            :key="index"
            class="dash-inquiry"
          >
            <APill :kind="getStatusPillKind(inquiry.statusRaw)">
              {{ getStatusLabel(inquiry.statusRaw) }}
            </APill>
            <div class="dash-inquiry__main">
              <p class="dash-inquiry__title">{{ inquiry.title }}</p>
              <span class="dash-inquiry__cat">{{ inquiry.category }}</span>
            </div>
            <span class="dash-inquiry__date">{{ inquiry.date }}</span>
          </li>
        </ul>
        <AEmpty v-else title="최근 문의 내역이 없습니다" />
      </ACard>
    </div>

    <!-- Inquiry Categories -->
    <ACard
      title="카테고리별 문의 현황"
      :sub="`전체 ${inquiryCategoryTotal.toLocaleString()}건`"
      :padding="20"
    >
      <ul v-if="inquiryCategories.length" class="dash-bars">
        <li
          v-for="cat in inquiryCategories"
          :key="cat.category"
          class="dash-bar"
        >
          <span class="dash-bar__label">{{ cat.category_label || cat.category }}</span>
          <span class="dash-bar__track">
            <span class="dash-bar__fill" :style="{ width: categoryBarWidth(cat.count) }" />
          </span>
          <span class="dash-bar__value">{{ (cat.count || 0).toLocaleString() }}</span>
        </li>
      </ul>
      <AEmpty v-else title="문의 데이터가 없습니다" />
    </ACard>

    <!-- Recent Settlements -->
    <ACard title="최근 정산 내역" sub="최근 처리된 정산" :padding="0">
      <template #actions>
        <ABtn kind="primary" icon="list" @click="$router.push('/settlement/completed')">
          전체 보기
        </ABtn>
      </template>

      <ATable
        :columns="settlementCols"
        :rows="recentSettlements"
        empty-text="정산 내역이 없습니다"
        row-key="id"
      >
        <template #cell:date="{ row }">
          <div class="dash-content-cell">
            <div class="dash-content-cell__title">{{ row.date }}</div>
            <div class="dash-content-cell__sub">{{ row.period }}</div>
          </div>
        </template>

        <template #cell:target="{ row }">
          <div class="dash-content-cell">
            <div class="dash-content-cell__title">{{ row.target }}</div>
            <div class="dash-content-cell__sub">{{ row.type }}</div>
          </div>
        </template>

        <template #cell:amount="{ value }">
          <span class="dash-mono dash-revenue">{{ formatCurrency(value) }}</span>
        </template>

        <template #cell:status="{ value }">
          <APill :kind="getSettlementPillKind(value)">{{ value }}</APill>
        </template>
      </ATable>
    </ACard>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import APill from "@/components/Pineapple/APill.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APieChart from "@/components/Pineapple/charts/APieChart.vue";
import ALineChart from "@/components/Pineapple/charts/ALineChart.vue";

// APieChart 의 기본 팔레트와 동일한 순서 — 범례 점 색을 슬라이스와 맞추기 위함
const SLICE_COLORS = ['#FFC93C', '#3B7BC4', '#1E8C66', '#C44A3A', '#1F1B14', '#9C9486'];

// 인세 정산은 2026-09-01 결제분부터 개시되고, 정산 배치는 매월 1일 02:00 에 전월분을
// 계산한다. 즉 첫 판매량 집계는 2026-10-01 02:00 배치 이후에 생기며 그 전까지는
// 실제 판매가 있어도 API 가 0 을 내려준다.
// (대시보드_에디터_API.md §5 집계 기준 — 이 시각이 지나면 자동으로 해제된다)
const SETTLEMENT_FIRST_BATCH_AT = new Date('2026-10-01T02:00:00');
const SETTLEMENT_PENDING_NOTICE =
  '인세 정산은 2026-09-01 결제분부터 개시됩니다. 2026-10-01 첫 정산 배치 이후 판매량이 집계됩니다.';

export default {
  name: "Sales",
  components: {
    APageHead,
    ACard,
    AStat,
    ATable,
    APill,
    ABtn,
    AEmpty,
    APieChart,
    ALineChart,
  },
  data() {
    return {
      loading: false,
      lastLoadedAt: null,
      chartDataReady: false,
      chartKey: 0,
      stats: {
        textbooks: 0,
        textbooksChange: 0,
        audiobooks: 0,
        videos: 0,
        faqs: 0,
        inquiries: 0,
        pendingInquiries: 0,
        monthlySettlement: 0,
        settlementCount: 0,
        settlementChange: 0,
      },
      statsFromApi: false,
      inquiries: [],
      inquiryCategories: [],
      inquiryCategoryTotalApi: 0,
      faqs: [],
      monthlyTrend: [],
      topContents: [],
      settlements: [],
      topContentsCols: [
        { key: 'rank',    label: '순위',     align: 'center', width: 70 },
        { key: 'title',   label: '콘텐츠',   align: 'left' },
        { key: 'type',    label: '타입',     align: 'center', width: 120 },
        { key: 'sales',   label: '판매량',   align: 'right',  width: 120 },
      ],
      settlementCols: [
        { key: 'date',   label: '정산일',     align: 'left' },
        { key: 'target', label: '대상',       align: 'left' },
        { key: 'amount', label: '정산 금액',  align: 'right', width: 160 },
        { key: 'status', label: '상태',       align: 'center', width: 120 },
      ],
    };
  },
  computed: {
    // 첫 정산 배치 전에는 API 가 항상 0 을 주므로 '판매 0건' 으로 오인되지 않게 한다.
    // 현재 시각 대신 lastLoadedAt 을 기준으로 삼아 새로고침 때마다 다시 평가되게 한다.
    settlementAggregated() {
      return this.lastLoadedAt ? this.lastLoadedAt >= SETTLEMENT_FIRST_BATCH_AT : false;
    },
    settlementPendingNotice() {
      return SETTLEMENT_PENDING_NOTICE;
    },
    // 정산 확정 전 수익금은 표시하지 않는다 — 비중은 판매량 기준으로만 낸다
    contentShare() {
      if (!this.settlementAggregated) return [];
      const rows = this.topContents
        .map(item => ({ title: item.title || '-', value: Number(item.sales) || 0 }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);
      const total = rows.reduce((sum, item) => sum + item.value, 0);
      return rows.map((item, index) => ({
        ...item,
        color: SLICE_COLORS[index % SLICE_COLORS.length],
        percent: total ? Math.round((item.value / total) * 1000) / 10 : 0,
      }));
    },
    contentShareChart() {
      return {
        labels: this.contentShare.map(item => item.title),
        datasets: {
          label: '판매량',
          data: this.contentShare.map(item => item.value),
        },
      };
    },
    contentShareFoot() {
      if (!this.settlementAggregated) {
        return SETTLEMENT_PENDING_NOTICE;
      }
      if (!this.contentShare.length) {
        return '판매량이 집계되면 교재별 비중이 표시됩니다.';
      }
      const total = this.contentShare.reduce((sum, item) => sum + item.value, 0);
      return `상위 ${this.contentShare.length}종 합계 ${total.toLocaleString('ko-KR')}건 판매`;
    },
    settlementChartData() {
      if (!this.monthlyTrend.length) {
        return { labels: [], datasets: [] };
      }
      return {
        labels: this.monthlyTrend.map(item => item.month_label),
        datasets: [
          {
            label: '정산 금액 (만원)',
            data: this.monthlyTrend.map(item => item.amount / 10000),
          },
          {
            label: '정산 건수',
            data: this.monthlyTrend.map(item => item.count),
          },
        ],
      };
    },
    inquiryCategoryTotal() {
      if (this.inquiryCategoryTotalApi) return this.inquiryCategoryTotalApi;
      return this.inquiryCategories.reduce((sum, cat) => sum + (cat.count || 0), 0);
    },
    maxCategoryCount() {
      return this.inquiryCategories.reduce((max, cat) => Math.max(max, cat.count || 0), 0);
    },
    recentInquiries() {
      if (!this.inquiries.length) {
        return [];
      }
      return this.inquiries.slice(0, 5).map(inq => ({
        title: inq.subject,
        category: inq.category,
        status: inq.status,
        statusRaw: inq.status,
        date: this.formatDate(inq.created_at),
      }));
    },
    recentSettlements() {
      return this.settlements.map(item => ({
        id: item.id,
        date: item.settlement_date,
        period: item.period,
        target: item.target_name,
        type: item.settlement_type,
        amount: item.amount,
        status: item.status_label,
      }));
    },
  },
  mounted() {
    this.loadDashboardData();
  },
  methods: {
    async loadDashboardData() {
      this.loading = true;
      await Promise.all([
        this.loadStats(),
        this.loadMonthlyTrend(),
        this.loadTopContents(),
        this.loadRecentSettlements(),
        this.loadInquiries(),
        this.loadInquiryCategories(),
      ]);
      this.chartKey++;
      this.chartDataReady = true;
      this.lastLoadedAt = new Date();
      this.loading = false;
    },
    async loadStats() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/editor/dashboard/stats',
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          const data = res.data.data || res.data;
          if (data.contents) {
            this.stats.textbooks = data.contents.textbooks || 0;
            this.stats.audiobooks = data.contents.audiobooks || 0;
            this.stats.videos = data.contents.videos || 0;
            this.stats.faqs = data.contents.faqs || 0;
          }
          if (data.contents_change) {
            this.stats.textbooksChange = data.contents_change.textbooks_change || 0;
          }
          if (data.inquiries) {
            this.stats.inquiries = data.inquiries.total || 0;
            this.stats.pendingInquiries = data.inquiries.pending || 0;
            // 문의 통계는 대시보드 API 가 기준 — helpdesk 목록 응답이 덮어쓰지 않도록 표시
            this.statsFromApi = true;
          }
          if (data.settlement) {
            this.stats.monthlySettlement = data.settlement.monthly_amount || 0;
            this.stats.settlementCount = data.settlement.monthly_count || 0;
            this.stats.settlementChange = data.settlement.change_percentage || 0;
          }
        }
      } catch (error) {
        console.error('Stats API error:', error);
        await this.loadStatsFallback();
      }
    },
    async loadStatsFallback() {
      const userInfo = this.$store.state.auth?.user_info || {};
      const editorIdx = userInfo.idx;
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/textbooks',
          params: { editor_idx: editorIdx, limit: 1, page: 1 },
          loadingBar: false,
        });
        if (res.status === 200) this.stats.textbooks = res.data.total_count || 0;
      } catch (error) { /* ignore */ }
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/audiobooks',
          params: { editor_idx: editorIdx, limit: 1, page: 1 },
          loadingBar: false,
        });
        if (res.status === 200) this.stats.audiobooks = res.data.total_count || 0;
      } catch (error) { /* ignore */ }
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/videos',
          params: { editor_idx: editorIdx, limit: 1, page: 1 },
          loadingBar: false,
        });
        if (res.status === 200) this.stats.videos = res.data.total_count || 0;
      } catch (error) { /* ignore */ }
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/helpdesk/faqs',
          loadingBar: false,
        });
        if (res.status === 200 && res.data.faqs) this.stats.faqs = res.data.faqs.length;
      } catch (error) { /* ignore */ }
    },
    async loadMonthlyTrend() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/editor/dashboard/settlement/monthly-trend',
          params: { months: 6 },
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          const data = res.data.data || res.data;
          this.monthlyTrend = data.trend || [];
        }
      } catch (error) {
        console.error('[월별정산] API error:', error);
      }
    },
    // 확정 필드는 sales 하나다 (대시보드_에디터_API.md §5).
    // API 는 정산 행이 없어도 항상 0 을 주고, 폴백(교재 목록)에는 판매 필드 자체가
    // 없어 null(미집계)이 된다 - 화면에서 '-' 로 구분 표시된다.
    normalizeTopContent(item) {
      return {
        ...item,
        title: item.title,
        author: item.author || item.editor_name || '',
        type: item.type || 'textbook',
        sales: item.sales == null ? null : Number(item.sales) || 0,
      };
    },
    async loadTopContents() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/editor/dashboard/contents/top',
          params: { limit: 5, sort_by: 'sales' },
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          const data = res.data.data || res.data;
          this.topContents = (data.contents || []).map(this.normalizeTopContent);
        }
      } catch (error) {
        console.error('[인기콘텐츠] API error:', error);
        await this.loadTopContentsFallback();
      }
    },
    async loadTopContentsFallback() {
      const userInfo = this.$store.state.auth?.user_info || {};
      const editorIdx = userInfo.idx;
      const contents = [];
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/textbook/editor/textbooks',
          params: { editor_idx: editorIdx, limit: 5, page: 1 },
          loadingBar: false,
        });
        // 교재 목록 응답의 배열 키는 items (ContentsTextbooks.vue 와 동일)
        if (res.status === 200 && res.data.items) {
          res.data.items.forEach(item => {
            // 통계 API 가 없을 때 임의 값을 지어내지 않는다 (대시보드_에디터_API.md - mock 제거)
            // 교재 목록에는 판매 필드가 없으므로 sales 는 null(미집계)로 남는다
            contents.push(this.normalizeTopContent({ ...item, type: 'textbook' }));
          });
        }
      } catch (error) { /* ignore */ }
      this.topContents = contents
        .sort((a, b) => (b.sales || 0) - (a.sales || 0))
        .slice(0, 5);
    },
    async loadRecentSettlements() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/editor/dashboard/settlement/recent',
          params: { limit: 5 },
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          const data = res.data.data || res.data;
          this.settlements = data.settlements || [];
        }
      } catch (error) {
        console.error('Recent settlements API error:', error);
      }
    },
    async loadInquiries() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/helpdesk/inquiries',
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          this.inquiries = res.data.inquiries || [];
          if (res.data.statistics && !this.statsFromApi) {
            this.stats.inquiries = res.data.pagination?.total || this.inquiries.length;
            this.stats.pendingInquiries = res.data.statistics.pending || 0;
          }
        }
      } catch (error) {
        console.error('Inquiries API error:', error);
      }
    },
    async loadInquiryCategories() {
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/editor/dashboard/inquiries/by-category',
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          const data = res.data.data || res.data;
          this.inquiryCategories = data.categories || [];
          this.inquiryCategoryTotalApi = data.total || 0;
        }
      } catch (error) {
        console.error('[문의 카테고리] API error:', error);
      }
    },
    categoryBarWidth(count) {
      if (!this.maxCategoryCount) return '0%';
      const ratio = (count || 0) / this.maxCategoryCount;
      return `${Math.max(ratio * 100, count ? 4 : 0)}%`;
    },
    formatCurrency(value) {
      if (!value) return '₩0';
      return '₩' + value.toLocaleString('ko-KR');
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },
    formatTime(date) {
      if (!date) return '';
      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    getStatusPillKind(status) {
      const map = {
        pending: 'warn',
        in_progress: 'info',
        answered: 'ok',
        closed: 'default',
      };
      return map[status] || 'default';
    },
    getStatusLabel(status) {
      const labels = {
        pending: '대기',
        in_progress: '처리중',
        answered: '답변완료',
        closed: '종료',
      };
      return labels[status] || status;
    },
    getSettlementPillKind(status) {
      const map = {
        '완료': 'ok',
        '대기': 'warn',
        '처리중': 'info',
        '반려': 'err',
      };
      return map[status] || 'default';
    },
    getContentTypeKind(type) {
      const map = {
        textbook: 'info',
        audiobook: 'yellow',
        video: 'dark',
      };
      return map[type] || 'default';
    },
    getContentTypeMatIcon(type) {
      const map = {
        textbook: 'menu_book',
        audiobook: 'headphones',
        video: 'play_circle',
      };
      return map[type] || 'description';
    },
    getContentTypeLabel(type) {
      const labels = {
        textbook: '교재',
      };
      return labels[type] || type;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.dash-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: $p-font-body;
}

.dash-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.dash-charts {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 16px;
}

.dash-mid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 16px;
}

// Pie chart layout
.dash-pie {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 16px;
  align-items: center;
}

.dash-pie__chart {
  min-height: 220px;
  display: grid;
  place-items: center;
}

.dash-loading {
  height: 200px;
  display: grid;
  place-items: center;
}

.p-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid $p-line;
  border-top-color: $p-point;
  border-radius: 50%;
  animation: dash-spin 0.7s linear infinite;
}
@keyframes dash-spin { to { transform: rotate(360deg); } }

.dash-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;
  }
}

.dash-legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &--info  { background: $p-info; }
  &--point { background: $p-point; }
  &--ink   { background: $p-ink; }
  &--err   { background: $p-err; }
  &--ok    { background: $p-ok; }
}

.dash-line-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: $p-ink2;
}

.dash-legend__label {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: $p-ink2;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-legend__value {
  font-family: $p-font-mono;
  font-size: 14px;
  font-weight: 800;
  color: $p-ink;
}

// Line chart
.dash-line-legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.dash-line__wrap {
  min-height: 280px;
  display: grid;
  place-items: stretch;
}

.dash-card-foot {
  margin: 0;
  font-size: 12px;
  color: $p-ink-mid;
  strong { color: $p-point-deep; font-weight: 800; }
}

// Top contents rank chip
.dash-rank {
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-family: $p-font-mono;
  font-size: 13px;
  font-weight: 800;
  background: $p-panel-alt;
  color: $p-ink-mid;
  border: 1px solid $p-line;

  &--top {
    background: $p-point;
    color: $p-ink;
    border-color: $p-point-deep;
  }
}

.dash-content-cell__title {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
  line-height: 1.3;
}

.dash-content-cell__sub {
  font-size: 11px;
  color: $p-ink-soft;
  margin-top: 2px;
}

.dash-mono {
  font-family: $p-font-mono;
  font-weight: 700;
  font-size: 13px;
  color: $p-ink;
}

.dash-revenue {
  color: $p-ok;
}

// 집계값이 없을 때(미집계) 표시 - 판매 0건과 구분된다
.dash-muted {
  color: $p-ink-soft;
  font-weight: 400;
}

.dash-updated {
  font-size: 12px;
  color: $p-ink-soft;
  white-space: nowrap;
}

// Inquiry category bars
.dash-bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dash-bar {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) 64px;
  align-items: center;
  gap: 12px;
}

.dash-bar__label {
  font-size: 12px;
  font-weight: 700;
  color: $p-ink2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-bar__track {
  height: 10px;
  border-radius: 999px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  overflow: hidden;
}

.dash-bar__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: $p-point;
  transition: width 0.3s ease;
}

.dash-bar__value {
  font-family: $p-font-mono;
  font-size: 13px;
  font-weight: 800;
  color: $p-ink;
  text-align: right;
}

@media (max-width: 700px) {
  .dash-bar { grid-template-columns: 96px minmax(0, 1fr) 52px; }
}

// Inquiries list
.dash-inquiries {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.dash-inquiry {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid $p-line-soft;
  &:last-child { border-bottom: 0; }
}

.dash-inquiry__main {
  flex: 1;
  min-width: 0;
}

.dash-inquiry__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-inquiry__cat {
  font-size: 11px;
  color: $p-ink-soft;
}

.dash-inquiry__date {
  font-family: $p-font-mono;
  font-size: 11px;
  color: $p-ink-soft;
  flex-shrink: 0;
}

// "전체보기 →" link in card actions
.dash-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  color: $p-point-deep;
  text-decoration: none;
  transition: color 0.12s ease;

  i { font-size: 14px !important; }

  &:hover {
    color: $p-ink;
    text-decoration: underline;
  }
}

// Responsive folding
@media (max-width: 1100px) {
  .dash-stats   { grid-template-columns: 1fr; }
  .dash-charts  { grid-template-columns: 1fr; }
  .dash-mid     { grid-template-columns: 1fr; }
  .dash-pie     { grid-template-columns: 1fr; }
}
</style>
